import { useEffect, useState } from "react"
import { GetApp } from "../../App"
import { BlockRow, BlockField } from "../../Components/Block"
import { LocalizedMessage } from "../../Components/LocalizedMessage"
import { InfoDialog } from "../../Components/InfoDialog"
import { IEditPermissivenessLevel, IShipSettings } from "../../Types"

interface EditPermissivenessSettingViewProps {
  settings: IShipSettings;
  onEdited: () => void;
}

// `""` represents the unset/default state (`EditPermissivenessLevel.default` == `.strict`
// server-side). An explicit `"strict"` value is behaviorally identical, so both normalize to
// the same "Default (strict)" option -- the UI never shows two different-looking selections
// for the same behavior.
function toSelectValue(level: IEditPermissivenessLevel | undefined): string {
  if (level == undefined || level == IEditPermissivenessLevel.strict) {
    return "";
  }
  return level;
}

// Edit-permissiveness section of the settings tab (issue #1168). Lets a ship admin/owner
// choose how permissive log/day field editing is: the default (strict, NMEA-sourced fields
// locked outside the recency window), extended (recency window lifted, NMEA protection kept)
// or unrestricted (all field-level protection removed). Enforced server-side in
// `Sources/LogbookLocal/LogApi.swift`; this view is UI-only.
export function EditPermissivenessSettingView({ settings, onEdited }: EditPermissivenessSettingViewProps) {
  const app = GetApp()!;
  const t = app.getT("ShipSettingsView");
  const [level, setLevel] = useState<string>(toSelectValue(settings?.editPermissiveness));
  const [showInfo, setShowInfo] = useState(false);

  useEffect(() => {
    setLevel(toSelectValue(settings?.editPermissiveness));
  }, [settings?.editPermissiveness]);

  return (
    <>
      <hr></hr>
      <BlockRow>
        <h3 className="block-row-followed-by-block-row">{t("edit_permissiveness_title")}</h3>
      </BlockRow>
      <BlockRow className="block-row-checkboxes block-row-one-row">
        <BlockField label={t("edit_permissiveness_level")} labelAfter={false} labelFor="EditPermissivenessLevel">
          <select id="EditPermissivenessLevel" name="editPermissiveness" value={level} onChange={(e) => { setLevel(e.target.value); onEdited(); }}>
            <option value="">{t("edit_permissiveness_level_default")}</option>
            <option value={IEditPermissivenessLevel.extended}>{t("edit_permissiveness_level_extended")}</option>
            <option value={IEditPermissivenessLevel.unrestricted}>{t("edit_permissiveness_level_unrestricted")}</option>
          </select>
        </BlockField>
        {/* Button for more info */}
        <BlockField>
          <button type="button" className="button" onClick={() => setShowInfo(true)}>
            <span className="question-circle">?</span>  {t("more_info")}
          </button>
        </BlockField>
      </BlockRow>
      {showInfo && (
        <InfoDialog
          title={t("edit_permissiveness_title")}
          message={<LocalizedMessage message={(m) => m.shipSetting.edit_permissiveness} />}
          onClose={() => setShowInfo(false)}
          style="info"
        />
      )}
    </>
  );
}
